import { createClient } from '@connectrpc/connect';
import { StreamService } from '@towns-protocol/proto';
import { dlog } from '@towns-protocol/dlog';
import { getEnvVar, randomUrlSelector } from './utils';
import { DEFAULT_RETRY_PARAMS, getRetryDelayMs, loggingInterceptor, retryInterceptor, } from './rpcInterceptors';
import { unpackMiniblock, unpackSnapshot } from './sign';
import { createHttp2ConnectTransport } from './rpcCommon';
import { streamIdAsBytes } from './id';
const logInfo = dlog('csb:rpc:info');
let nextRpcClientNum = 0;
export function makeStreamRpcClient(dest, refreshNodeUrl, opts) {
    const transportId = nextRpcClientNum++;
    const retryParams = opts?.retryParams ?? DEFAULT_RETRY_PARAMS;
    logInfo('makeStreamRpcClient, transportId =', transportId);
    const url = randomUrlSelector(dest);
    logInfo('makeStreamRpcClient: Connecting to url=', url, ' allUrls=', dest);
    const options = {
        baseUrl: url,
        interceptors: [
            ...(opts?.interceptors ?? []),
            loggingInterceptor(transportId),
            retryInterceptor({ ...retryParams, refreshNodeUrl }),
        ],
        defaultTimeoutMs: undefined, // default timeout is undefined, we add a timeout in the retryInterceptor
    };
    if (getEnvVar('RIVER_DEBUG_TRANSPORT') !== 'true') {
        options.useBinaryFormat = true;
    }
    else {
        logInfo('makeStreamRpcClient: running in debug mode, using JSON format');
        options.useBinaryFormat = false;
        options.jsonOptions = {
            alwaysEmitImplicit: true,
            useProtoFieldName: true,
        };
    }
    const transport = createHttp2ConnectTransport(options);
    const client = createClient(StreamService, transport);
    client.url = url;
    client.opts = { retryParams };
    return client;
}
export function getMaxTimeoutMs(opts) {
    let maxTimeoutMs = 0;
    for (let i = 1; i <= opts.retryParams.maxAttempts; i++) {
        maxTimeoutMs +=
            opts.retryParams.defaultTimeoutMs ?? 0 + getRetryDelayMs(i, opts.retryParams);
    }
    return maxTimeoutMs;
}
export async function getMiniblocks(client, streamId, fromInclusive, toExclusive, omitSnapshots, unpackEnvelopeOpts) {
    const allMiniblocks = [];
    let currentFromInclusive = fromInclusive;
    let reachedTerminus = false;
    const parsedSnapshots = {};
    while (currentFromInclusive < toExclusive) {
        const { miniblocks, terminus, nextFromInclusive, snapshots } = await fetchMiniblocksFromRpc(client, streamId, currentFromInclusive, toExclusive, omitSnapshots, unpackEnvelopeOpts);
        allMiniblocks.push(...miniblocks);
        if (!omitSnapshots) {
            Object.entries(snapshots).forEach(([key, snapshot]) => {
                parsedSnapshots[key] = snapshot;
            });
        }
        // Set the terminus to true if we got at least one response with reached terminus
        // The behaviour around this flag is not implemented yet
        if (terminus && !reachedTerminus) {
            reachedTerminus = true;
        }
        if (currentFromInclusive === nextFromInclusive) {
            break;
        }
        currentFromInclusive = nextFromInclusive;
    }
    return {
        miniblocks: allMiniblocks,
        terminus: reachedTerminus,
        snapshots: parsedSnapshots,
    };
}
async function fetchMiniblocksFromRpc(client, streamId, fromInclusive, toExclusive, omitSnapshots, unpackEnvelopeOpts) {
    const response = await client.getMiniblocks({
        streamId: streamIdAsBytes(streamId),
        fromInclusive,
        toExclusive,
        omitSnapshots,
    });
    const miniblocks = [];
    const parsedSnapshots = {};
    for (const miniblock of response.miniblocks) {
        const unpackedMiniblock = await unpackMiniblock(miniblock, unpackEnvelopeOpts);
        const unpackedMiniblockNum = unpackedMiniblock.header.miniblockNum.toString();
        miniblocks.push(unpackedMiniblock);
        if (!omitSnapshots && response.snapshots[unpackedMiniblockNum]) {
            parsedSnapshots[unpackedMiniblockNum] = (await unpackSnapshot(unpackedMiniblock, response.snapshots[unpackedMiniblockNum], unpackEnvelopeOpts)).snapshot;
        }
    }
    const respondedFromInclusive = miniblocks.length > 0 ? miniblocks[0].header.miniblockNum : fromInclusive;
    return {
        miniblocks: miniblocks,
        terminus: response.terminus,
        nextFromInclusive: respondedFromInclusive + BigInt(response.miniblocks.length),
        snapshots: parsedSnapshots,
    };
}
//# sourceMappingURL=makeStreamRpcClient.js.map